// frontend/src/pages/admin/UsersPage.js
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaEdit, FaUserShield, FaUserAltSlash, FaSearch, FaKey } from 'react-icons/fa';
import api from '../../utils/api';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userToUpdate, setUserToUpdate] = useState(null);
  const [userToReset, setUserToReset] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  
  const { user } = useSelector(state => state.auth);
  
  // Check if current user is super admin
  const isSuperAdmin = user?.is_super_admin || false;
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Apply search filter when searchTerm changes
  useEffect(() => {
    if (searchTerm) {
      const results = users.filter(user => 
        user.phone_number.includes(searchTerm)
      );
      setFilteredUsers(results);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      console.log('Users data:', response.data); // Debug log
      setUsers(response.data);
      setFilteredUsers(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to fetch users');
      setLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchLoading(true);
    // Client-side filtering is already handled by useEffect
    // This is just for visual feedback
    setTimeout(() => {
      setSearchLoading(false);
    }, 300);
  };
  
  const handleAdminToggle = (selectedUser) => {
    // Don't allow non-super admins to change admin status
    if (!isSuperAdmin) {
      setError('您没有权限修改管理员状态。只有超级管理员可以执行此操作。');
      return;
    }
    
    // Don't allow self-demotion
    if (selectedUser.id === user.id && selectedUser.is_admin) {
      setError('您不能移除自己的管理员状态');
      return;
    }
    
    setUserToUpdate(selectedUser);
    setShowConfirmModal(true);
  };
  
  const handleResetPassword = (selectedUser) => {
    setUserToReset(selectedUser);
    setShowResetModal(true);
  };
  
  const confirmAdminUpdate = async () => {
    try {
      setLoading(true);
      const newAdminStatus = !userToUpdate.is_admin;
      
      const response = await api.put(`/users/${userToUpdate.id}/admin-status`, {
        is_admin: newAdminStatus
      });
      
      console.log('Admin status update response:', response.data); // Debug log
      
      // Update the local users state
      const updatedUsers = users.map(u => 
        u.id === userToUpdate.id ? { ...u, is_admin: newAdminStatus } : u
      );
      setUsers(updatedUsers);
      setFilteredUsers(
        searchTerm 
          ? updatedUsers.filter(u => u.phone_number.includes(searchTerm))
          : updatedUsers
      );
      
      setSuccess(`用户 "${userToUpdate.full_name || userToUpdate.phone_number}" 的管理员状态已更新`);
      setTimeout(() => setSuccess(null), 5000);
      
      setLoading(false);
      setShowConfirmModal(false);
      setUserToUpdate(null);
    } catch (err) {
      console.error('Error updating admin status:', err);
      setError(err.response?.data?.message || '更新用户失败');
      setLoading(false);
      setShowConfirmModal(false);
    }
  };
  
  const confirmResetPassword = async () => {
    try {
      setLoading(true);
      
      // Call the actual backend API
      const response = await api.post(`/users/${userToReset.id}/reset-password`);
      
      console.log('Password reset response:', response.data); // Debug log
      
      // Get the temporary password from the response
      const tempPassword = response.data.defaultPassword;
      setResetPassword(tempPassword);
      
      setSuccess(`用户 "${userToReset.full_name || userToReset.phone_number}" 的密码已重置为: ${tempPassword}`);
      setTimeout(() => {
        setSuccess(null);
        setResetPassword('');
      }, 10000); // Show for longer since it contains the password
      
      setLoading(false);
      setShowResetModal(false);
      setUserToReset(null);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.message || '重置密码失败');
      setLoading(false);
      setShowResetModal(false);
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
  };
  
  return (
    <div className="admin-users-page">
      <h1 className="text-3xl font-bold mb-6">用户管理</h1>
      
      {success && (
        <Message variant="success" onClose={() => setSuccess(null)}>
          {success}
          {resetPassword && (
            <div className="mt-2 p-2 bg-yellow-100 text-yellow-800 rounded font-mono">
              临时密码: {resetPassword}
            </div>
          )}
        </Message>
      )}
      {error && <Message variant="error" onClose={() => setError(null)}>{error}</Message>}
      
      {/* Search Box */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <form onSubmit={handleSearch} className="flex items-center">
          <div className="relative flex-grow">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜索用户手机号..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
            <div className="absolute left-3 top-2.5 text-gray-400">
              <FaSearch />
            </div>
          </div>
          <button
            type="submit"
            className="bg-blue-600 text-white ml-2 px-4 py-2 rounded-lg hover:bg-blue-700"
            disabled={searchLoading}
          >
            {searchLoading ? <Loader size="sm" /> : '搜索'}
          </button>
        </form>
      </div>
      
      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-6">
            <Loader />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    用户信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    电话
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    角色
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    注册日期
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      {searchTerm ? '没有找到匹配的用户' : '没有用户数据'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((userData) => (
                    <tr key={userData.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {userData.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {userData.full_name || '未设置姓名'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {userData.email || '未设置邮箱'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {userData.phone_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          userData.is_admin ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {userData.is_admin ? '管理员' : '普通用户'}
                        </span>
                        {userData.is_super_admin && (
                          <span className="ml-2 px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                            超级管理员
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(userData.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {/* Password Reset Button */}
                        <button
                          onClick={() => handleResetPassword(userData)}
                          className="px-2 py-1 rounded text-blue-600 hover:text-blue-900 mr-2"
                          title="重置密码"
                        >
                          <FaKey />
                        </button>
                        
                        {/* Admin Toggle Button - only active for super admin and not for super admins */}
                        {!userData.is_super_admin && (
                          <button
                            onClick={() => handleAdminToggle(userData)}
                            className={`px-2 py-1 rounded ${
                              !isSuperAdmin ? 'text-gray-300 cursor-not-allowed' : 
                              userData.is_admin ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                            }`}
                            title={userData.is_admin ? '移除管理员权限' : '授予管理员权限'}
                            disabled={!isSuperAdmin}
                          >
                            {userData.is_admin ? <FaUserAltSlash /> : <FaUserShield />}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Admin Toggle Confirmation Modal */}
      {showConfirmModal && userToUpdate && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">确认修改用户权限</h3>
            <p className="mb-4">
              您确定要{userToUpdate.is_admin ? '移除' : '授予'} "{userToUpdate.full_name || userToUpdate.phone_number}" 的管理员权限吗？
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={confirmAdminUpdate}
                className={`px-4 py-2 rounded-md text-white ${
                  userToUpdate.is_admin ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Password Reset Confirmation Modal */}
      {showResetModal && userToReset && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">确认重置密码</h3>
            <p className="mb-4">
              您确定要重置 "{userToReset.full_name || userToReset.phone_number}" 的密码吗？
              系统将生成一个临时密码，请确保记录下来并告知用户。
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={confirmResetPassword}
                className="px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                确认重置
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;